import { useLocation } from 'react-router';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { professionalTabBase } from '@modules/professional/util/professional-tab-base';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { DetailSaveButton, detailCta } from '~/ui-rocco/pages/detail-shared';
import { CONTENT_CAP_PADDED } from '~/ui-rocco/pages/detail-shared/large-screen';

/** The product page's Save button surface: white, lifted, no hairline. */
const SAVE_LIFTED =
    'border-transparent bg-white text-[#181818] [box-shadow:0px_4px_14px_10px_rgba(0,0,0,0.05)] hover:bg-white/70';

/**
 * The product page's CTA cluster on the profile: the practice's own enquiry
 * label (Get in Touch, Request a Quote — whatever they configured), Visit
 * Website and Save, ranged right under the badge, level with the statement's
 * first line as the product's are — and sticky, so once the
 * page has scrolled them up to the tab band they lock into its right end and
 * ride there for the rest of the page.
 *
 * A page-level sibling rather than part of the summary block: a sticky
 * element only travels as far as its own parent, and inside that block it
 * would come unstuck the moment the block scrolled away.
 *
 * The margins are what let it sit on the statement's line without occupying
 * it: the top one puts the pair 145px under the banner, where the product's
 * CTAs sit under its picture (the band is 92px tall against the trail's 65,
 * so 45 here is the product's 80 there); the bottom one gives the pair's own
 * height back, so the summary starts exactly where it otherwise would. The lock point is the band's midline: the band is
 * 92px tall and the pill 52px, so 20px down puts the two on one centre.
 *
 * Only the Overview has the statement to share a line with. On every other
 * tab the pair starts on the band's line instead, left of the badge where the
 * Overview shows the profile's figures, so the tab's content opens directly
 * under the band as before.
 */
export const ProfessionalStickyCtas = () => {
    const initial = useProfessionalInitialRouteData();
    const { pathname } = useLocation();
    // Gated on the professional's enquiry audience, as every enquiry CTA is.
    const { showEnquiryCTA } = useEnquiryVisibility(initial);
    const { openEnquiryModal } = useEnquiryModal({
        professional: initial,
        itemType: 'professional',
        siteTreeID: initial?.ID,
        itemTitle: initial?.Title,
        includeBranch: true,
    });
    if (!initial) return null;

    const enquiryLabel =
        initial.primaryCTA?.primaryCTALabelTitleCase || 'Get in Touch';

    // Preview-aware: a hardcoded public path never matches inside the
    // Business Manager preview, which gave the pair the non-overview
    // geometry on the preview's overview and rode it up over the band.
    const overview =
        pathname.replace(/\/+$/, '') ===
        professionalTabBase(initial.URLSegment, pathname);

    return (
        <div
            className={cn(
                'pointer-events-none sticky z-[120] w-full max-md:hidden md:top-[1.25rem] md:px-[3rem]',
                overview
                    ? 'md:mt-[2.8125rem] md:mb-[-3.25rem]'
                    : 'md:-mt-[4.5rem] md:mb-[1.25rem]',
            )}
        >
            <div
                className={cn(
                    CONTENT_CAP_PADDED,
                    'flex items-center justify-end gap-2',
                    // 48px clear of the badge's column, as the figures are.
                    !overview && 'md:pr-[16.5625rem]',
                )}
            >
                {showEnquiryCTA ? (
                    <button
                        type="button"
                        onClick={() =>
                            openEnquiryModal({
                                title: enquiryLabel,
                                ctaLabel: enquiryLabel,
                            })
                        }
                        className={detailCta(
                            'white-lifted',
                            'pointer-events-auto',
                        )}
                    >
                        {enquiryLabel}
                    </button>
                ) : null}
                {initial.Website ? (
                    <RoccoLink
                        to={initial.Website}
                        newTab
                        className={detailCta(
                            'white-lifted',
                            'pointer-events-auto',
                        )}
                    >
                        Visit Website
                    </RoccoLink>
                ) : null}
                <DetailSaveButton
                    pinId={initial.ID}
                    pinType="ProfessionalPin"
                    hoverLabel
                    className={cn(SAVE_LIFTED, 'pointer-events-auto')}
                />
            </div>
        </div>
    );
};
